import React, {useRef, useEffect, useState} from 'react';
import { ChevronDown } from 'lucide-react';
import { ChatTheme, Question } from '@/types/theme';
import { StreamingMessageGroup } from './StreamingMessageGroup';
import { QuestionOptions } from './QuestionOptions';
import { OptionsDisplay } from './OptionsDisplay';
import { debugLog } from '@/utils/browserLogger';

/**
 * Props for MessageList component
 * Following IntentDrive.ai TypeScript standards
 */
interface MessageListProps {
  messages: Array<{
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
  }>;
  streamingMessageIds?: Record<string, boolean>;
  isTyping?: boolean;
  theme?: ChatTheme;
  avatarUrl?: string | null;
  operatorName?: string;
  typingSpeed?: number;
  className?: string;
  autoScroll?: boolean;
  currentQuestion?: Question | null;
  onOptionSelect?: (value: string) => void;
  onMessageUnread?: (value: number) => void;
  showTimestamp?: boolean;
  onActionButtonClick?: (action: { type: 'url' | 'phone'; text: string; value: string }) => void;
}

/**
 * Message list that groups messages and handles auto-scrolling
 * Follows IntentDrive.ai component patterns
 */
export function MessageList({
  messages,
  streamingMessageIds = {},
  isTyping = false,
  theme,
  avatarUrl,
  operatorName = 'Assistant',
  typingSpeed = 400,
  className = '',
  autoScroll = true,
  currentQuestion = null,
  onOptionSelect,
  onMessageUnread,
  showTimestamp = true,
  onActionButtonClick,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const prevMessageCount = useRef(messages.length);
  const [optionsMessageId, setOptionsMessageId] = React.useState<string | null>(null);
  const hasStreamingMessage = React.useMemo(
    () => Object.values(streamingMessageIds).some(Boolean),
    [streamingMessageIds]
  );

  React.useEffect(() => {
    onMessageUnread(unreadCount);
  }, [onMessageUnread, unreadCount]);

  // Clear options when new user message is added
  React.useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.sender === 'user') {
      // Clear options when user responds
      setOptionsMessageId(null);
    }
  }, [messages]);

  // Group messages by sender
  const groupedMessages = React.useMemo(() => {
    return messages.reduce((acc: Array<{
      id: string;
      sender: 'user' | 'bot';
      messages: Array<{
        id: string;
        text: string;
        sender: 'user' | 'bot';
        timestamp: Date;
      }>;
    }>, message) => {
      const lastGroup = acc[acc.length - 1];

      // If this is the first message or sender changed, create a new group
      if (!lastGroup || lastGroup.sender !== message.sender) {
        acc.push({
          id: `group-${message.id}`,
          sender: message.sender,
          messages: [message]
        });
      } else {
        // Add to existing group
        lastGroup.messages.push(message);
      }

      return acc;
    }, []);
  }, [messages]);

  // Detect scroll position to determine if we should auto-scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Within 100px of bottom, we consider it "at bottom"
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShouldAutoScroll(isAtBottom);

      // If scrolled up and we have unread messages, reset the counter
      if (isAtBottom && unreadCount > 0) {
        setUnreadCount(0);
      }

      // Show button when scrolled up more than 200px
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 200);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [unreadCount]);

  // Track new messages when user is scrolled up
  useEffect(() => {
    if (messages.length > prevMessageCount.current && !shouldAutoScroll) {
      setUnreadCount(prev => prev + (messages.length - prevMessageCount.current));
    }
    prevMessageCount.current = messages.length;
  }, [messages.length, shouldAutoScroll]);

  // Listen for the custom event to show options
  React.useEffect(() => {
    const handleShowOptions = (event: CustomEvent) => {
      const { messageId, questionId, questionType, questionText } = event.detail;
      debugLog('MessageList', 'Received showQuestionOptions event', {
        messageId,
        questionId,
        questionType,
        questionText: questionText?.substring(0, 30) + '...',
        timestamp: new Date().toISOString()
      });

      // Set data attributes on the message element but DO NOT modify the content
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        // Just log the message content for debugging
        const messageContent = messageElement.textContent || '';
        debugLog('MessageList', 'Message element found for options', {
          messageId,
          messageContent: messageContent.substring(0, 30) + '...',
          questionId,
          questionType,
          questionText: questionText?.substring(0, 30) + '...'
        });

        // We no longer modify the message content - this was causing the issue
        // Instead, we just set the data attributes and let the OptionsDisplay component handle it
      }

      setOptionsMessageId(messageId);
    };

    // Add event listener
    document.addEventListener('showQuestionOptions', handleShowOptions as EventListener);

    // Clean up
    return () => {
      document.removeEventListener('showQuestionOptions', handleShowOptions as EventListener);
    };
  }, []);

  // Scroll to bottom when messages change or typing starts
  useEffect(() => {
    if (autoScroll && shouldAutoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [
    messages.length,
    isTyping,
    autoScroll,
    shouldAutoScroll,
    // Also trigger on streaming changes to any message
    Object.values(streamingMessageIds).filter(Boolean).length
  ]);

  // Scroll to bottom function for manual triggers
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setUnreadCount(0);
      setShowScrollButton(false);
    }
  };

  return (
    <div className="message-list-container relative flex flex-col flex-1">
      <div
        ref={containerRef}
        className={`message-list ${className} overflow-y-auto flex-1 px-4 py-4`}
        style={{
          backgroundColor: theme?.backgroundcolor || '#FFFFFF',
          scrollBehavior: 'smooth',
          // height: '100%',
          overflowY: 'auto',
          // maxHeight: 'calc(100vh - 140px)'
        }}
        role="log"
        aria-live="polite"
      >
        {/* Message groups */}
        {groupedMessages.map((group) => (
          <StreamingMessageGroup
            key={group.id}
            messages={group.messages}
            streamingMessageIds={streamingMessageIds}
            theme={theme}
            avatarUrl={avatarUrl}
            operatorName={operatorName}
            typingSpeed={typingSpeed}
            showTimestamp={showTimestamp}
            onActionButtonClick={onActionButtonClick}
          />
        ))}

        {/* Question options - only show when the question has been fully displayed */}
        {currentQuestion && onOptionSelect && (
          <div
            className="question-options-container"
            style={{
              padding: '1rem 1rem 1rem 3rem',
              marginTop: '0.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            {/* Options display component */}
            <OptionsDisplay
              currentQuestion={currentQuestion}
              messages={messages}
              optionsMessageId={optionsMessageId}
              isTyping={isTyping}
              onOptionSelect={onOptionSelect}
              theme={theme}
            />
          </div>
        )}

        {/* Invisible div for scrolling reference */}
        <div ref={messagesEndRef} />
      </div>

      {/* Unread messages indicator */}
      { (showScrollButton || unreadCount > 0) && (
        <button
          className={`unread-messages-marker ${unreadCount > 0 ? 'unread-messages-marker-pulse' : ''}`}
          onClick={scrollToBottom}
          aria-label={`${unreadCount} unread messages. Click to scroll to bottom.`}
          style={{
            position: 'absolute',
            bottom: '10px',
            right: '20px',
            backgroundColor: theme?.primarycolor || '#497CFD',
            color: '#FFFFFF',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            border: 'none',
            cursor: 'pointer',
            zIndex: 10
          }}
        >
          <ChevronDown className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                backgroundColor: '#ff4757',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {unreadCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
